<template>
  <a-spin :spinning="saving">
    <a-form
      :form="form"
      class="product-form"
      :class="{ 'mb-5': hideButton }"
      layout="vertical"
      @submit.prevent="submit"
    >
      <a-row type="flex" :gutter="24">
        <a-col :span="24" :lg="{ span: layout.span }">
          <a-card id="info">
            <h5 class="mb-4">
              <span>Product information</span>
            </h5>
            <a-form-item class="pb-0" label="Product name">
              <a-input
                placeholder="Product Name"
                autoFocus
                v-decorator="[
                  'name',
                  {
                    rules: [{ required: true }]
                  }
                ]"
              />
            </a-form-item>

            <a-row type="flex" :gutter="24">
              <a-col :md="{ span: 12 }">
                <a-form-item label="Type">
                  <a-select
                    :options="productTypes"
                    v-decorator="[
                      'productType',
                      {
                        rules: [{ required: true }]
                      }
                    ]"
                    placeholder="Select Type"
                  />
                </a-form-item>
              </a-col>

              <a-col :md="{ span: 12 }">
                <a-form-item class="pb-0" label="Category">
                  <a-cascader
                    :options="categories"
                    v-decorator="[
                      'category',
                      {
                        rules: [{ required: true }]
                      }
                    ]"
                    placeholder="Select Category"
                  />
                </a-form-item>
              </a-col>

              <a-col :md="{ span: 12 }" v-for="(item, index) in productAttributesUsed" :key="index">
                <a-form-item class="pb-0" :label="item.attribute_name">
                  <a-input
                    v-if="item.attribute_type === 'number' || item.attribute_type === 'currency'"
                    :prefix="item.attribute_type === 'currency' ? 'Rp' : null"
                    @keypress="e => { if (!/\d/.test(e.key)) return e.preventDefault() }"
                    v-decorator="[
                      `${item.attribute_id}`,
                      {
                        initialValue: item.productattribute_value ? Number(item.productattribute_value) : null,
                      }
                    ]"
                  />

                  <a-date-picker
                    class="w-100"
                    show-time
                    v-if="item.attribute_type === 'date'"
                    format="YYYY-MMMM-DD HH:mm:ss"
                    v-decorator="[
                      `${item.attribute_id}`,
                      {
                        initialValue: item.productattribute_value ? $moment(item.productattribute_value * 1000).format('YYYY-MMMM-DD HH:mm:ss') : null,
                      }
                    ]"
                  />

                  <a-range-picker
                    class="w-100"
                    v-if="item.attribute_type === 'rangedate'"
                    v-decorator="[
                      `${item.attribute_id}`,
                      {
                        initialValue: null,
                      }
                    ]"
                  />

                  <a-textarea
                    class="w-100"
                    v-if="item.attribute_type === 'longtext'"
                    v-decorator="[`${item.attribute_id}`,
                      {
                        initialValue: item.productattribute_value ? item.productattribute_value : null,
                        rules: [{ maxLength: 5, message: 'too long' }]
                      }
                    ]"
                  />

                  <a-input
                    class="w-100"
                    v-if="item.attribute_type === 'text'"
                    v-decorator="[`${item.attribute_id}`,
                      {
                        initialValue: item.productattribute_value ? item.productattribute_value : null,
                      }
                    ]"
                  />
                </a-form-item>
              </a-col>
            </a-row>
          </a-card>

          <a-card class="mt-4" id="desription">
            <h5 class="mb-4">Product Description</h5>

            <a-form-item class label="Description" required>
              <TextEditor
                placeholder="Write product description"
                v-decorator="[
                  'description',
                  {
                    initialValue: '',
                    rules: [{ required: true, message: 'Description is required' }]
                  }
                ]"
              />
            </a-form-item>

            <a-form-item label="Short Description" class="mb-0">
              <a-textarea
                v-decorator="[
                'shortDescription',
                {
                  initialValue: '',
                  rules: [{ required: true, message: 'Short description is required' }, { maxLength: 3, message: 'too long' }]
                }
              ]"
              />
            </a-form-item>
          </a-card>

          <a-card class="mt-4" id="variant">
            <h5 class="mb-4">Product Variant</h5>

            <div class="ant-row-flex ant-row-flex-space-between ant-row-flex-middle">
              <div>
                Add product variant
              </div>
              <a-dropdown v-if="availableVariantOptions.length">
                <a-button icon="plus" type="bordered">
                  Add Variant
                </a-button>
                <a-menu
                  slot="overlay"
                  @click="addVariant"
                >
                  <a-menu-item
                    v-for="(variant) in availableVariantOptions"
                    :key="variant.index"
                  >
                    {{ variant.label }}
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </div>

            <a-row type="flex" class="variant-items__wrapper" :gutter="24">
              <a-col
                :span="24"
                :md="{ span: 12 }"
                :xl="{ span: 12 }"
                v-for="variant in selectedOptions"
                :key="variant.index"
                :variant-index="variant.index"
              >
                <div
                  class="variant-items__item"
                >
                  <legend>{{ variant.label }}</legend>
                  <div
                    v-for="(variantValue, variantValueIndex) in variant.values"
                    :key="`${variant.index}-${variantValueIndex}`"
                    class="variant-items__item__option"
                  >
                    <a-input
                      v-model="variant.values[variantValueIndex]"
                      :placeholder="variant.placeholder"
                      :suffix="`${(variant.values[variantValueIndex] || '').length} / 20`"
                      :maxLength="20"
                      autoFocus
                    >
                      <template #addonAfter>
                        <a-button
                          icon="delete"
                          type="link"
                          @click="removeVariantOption(variant, variantValueIndex)"
                          :disabled="!canRemoveVariantOption(variant, variantValueIndex)"
                        >
                        </a-button>
                      </template>
                    </a-input>
                  </div>

                  <div class="variant-items__item__footer ant-row-flex ant-row-flex-space-between">
                    <a-button size="small" class="add" icon="plus" @click="addVariantOption(variant)">
                      Add {{ variant.label }}
                    </a-button>

                    <!-- <a-button size="small" class="remove" icon="delete" @click="removeVariant(variant)">
                      Remove variant {{ variant.label }}
                    </a-button> -->
                  </div>
                </div>
              </a-col>
            </a-row>

          </a-card>

          <a-card class="mt-4" id="management">
            <h5 class="mb-4">Product Management</h5>

            <a-row>
              <a-col :md="{ span: 8 }">
                <a-form-item label="Channel">
                  <a-select
                    :options="channels"
                    v-decorator="[
                    'channel',
                    {
                      rules: [{ required: true }]
                    }
                  ]"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <div class="variant-table" v-if="hasVariations">
              <table>
                <thead>
                  <tr>
                    <th v-for="option in selectedOptions" :key="option.index">
                      {{ option.label }}
                    </th>
                    <th class="sku">
                      SKU
                    </th>
                    <th class="price">
                      Price
                    </th>
                    <th class="discount">
                      Discount
                    </th>
                    <td width="20" class="no-border"></td>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(sku, rowIndex) in skus">
                    <tr :key="`${rowIndex}-${sku.optionId}`">
                      <template
                        v-for="(option) in sku.options"
                      >
                        <td
                          :key="`${option.id}`"
                        >
                          {{ option.value }}
                        </td>
                      </template>
                      <td class="input-cell">
                        <a-form-item>
                          <a-input
                            v-decorator="[
                              `skus.${rowIndex}.sku`,
                              {
                                initialValue: sku.sku,
                                rules: [{ required: true, message: 'Field is required' }]
                              }
                            ]"
                          />
                        </a-form-item>
                      </td>
                      <td class="input-cell">
                        <a-form-item>
                          <a-input
                            :min="0"
                            prefix="Rp"
                            @keypress="e => { if (!/\d/.test(e.key)) return e.preventDefault() }"
                            v-decorator="[
                              `skus.${rowIndex}.price.msrp`,
                              {
                                initialValue: sku.price.msrp,
                                rules: [{ required: true, message: 'Field is required' }]
                              }
                            ]"
                          />
                        </a-form-item>
                      </td>
                      <td class="input-cell">
                        <div v-if="hasDiscountVariant(rowIndex)">
                          <div> {{ valueTableDiscountVariant(rowIndex) }} </div>
                          <div> {{ changeDateFormatDiscountVariant(rowIndex) }} </div>
                          <div style="cursor: pointer; color: blue;" @click="() => (setInitialValueDiscountVariant(rowIndex), modalDiscount = true, rowIndexVariantSku = rowIndex, skusEditDelete = form.getFieldValue(`skus.${rowIndex}.sku`))"> Edit Discount </div>
                          <div style="cursor: pointer; color: blue;" @click="() => (rowIndexVariantSku = rowIndex, skusEditDelete = form.getFieldValue(`skus.${rowIndex}.sku`), deleteDiscountVariant())"> Delete Discount </div>
                        </div>
                        <div v-if="!hasDiscountVariant(rowIndex)">
                          <div class="text-muted"> Belum ada discount </div>
                          <div style="cursor: pointer; color: blue;" @click="() => (modalDiscount = true, rowIndexVariantSku = rowIndex)"> Add Discount </div>
                        </div>
                      </td>
                      <!-- <td class="no-border text-center">
                        <a-button icon="delete" type="link" @click="deleteSku(rowIndex)"></a-button>
                      </td> -->
                    </tr>
                  </template>
                </tbody>
              </table>

              <a-modal
                style="top: 20px;"
                v-model="modalDiscount"
                title="Add Discount"
                :footer="null"
                @cancel="(e) => (initialValueDiscountVariant = null, formDiscountVarian.resetFields()) "
              >
                <a-form
                  :form="formDiscountVarian"
                  @submit.prevent="submitDiscountVariant"
                >
                  <a-row type="flex" :gutter="24">
                    <a-col :md="{ span: 24 }">
                      <a-form-item label="Set Discount Type">
                        <a-radio-group v-decorator="[
                          'discount_variant_type',
                          {
                            initialValue: initialValueDiscountVariant ? initialValueDiscountVariant.type : null,
                            rules: [{ required: true }]
                          }]"
                          @change="(e) => initialValueDiscountVariant ? initialValueDiscountVariant.type = null : null"
                        >
                          <a-radio :value="1">
                            Percentage
                          </a-radio>
                          <a-radio :value="2">
                            Fixed Amount
                          </a-radio>
                        </a-radio-group>
                      </a-form-item>
                    </a-col>
                    <a-col :md="{ span: 12 }">
                      <a-form-item
                        label="Discount Value"
                        class="mb-0"
                      >
                        <a-input
                          :min="0"
                          class="w-100"
                          v-decorator="[
                            `discount_variant`, {
                              initialValue: initialValueDiscountVariant ? initialValueDiscountVariant.value : null,
                              rules: [{
                                required: true,
                                pattern: new RegExp('^[0-9]*$'),
                                message: 'Invalid input'
                              }],
                            }
                          ]"
                          :suffix="initialValueDiscountVariant && initialValueDiscountVariant.type === 1 ? '%' : formDiscountVarian.getFieldValue('discount_variant_type') === 1 ? '%' : null"
                          :prefix="initialValueDiscountVariant && initialValueDiscountVariant.type === 2 ? 'Rp' : formDiscountVarian.getFieldValue('discount_variant_type') === 2 ? 'Rp': null"
                        />
                      </a-form-item>
                    </a-col>
                    <a-col :md="{ span: 24 }">
                      <a-form-item
                        label="Discount Periode"
                        class="mb-0"
                      >
                        <!-- <a-range-picker
                          v-decorator="[
                            `discount_periode_variant`,
                            {
                              rules: [{
                                type: 'array',
                                required: true,
                                message: 'Discount Periode is Required'
                              }],
                              initialValue: initialValueDiscountVariantDate(rowIndexVariantSku).date
                            }
                          ]"
                          dateFormat="DD MM YYYY"
                        /> -->
                        <a-date-picker
                          v-decorator="[
                            `discount_periode_variant_start`,
                            {
                              rules: [{
                                required: true,
                                message: 'Discount Periode is Required'
                              }],
                              initialValue: initialValueDiscountVariantDate(rowIndexVariantSku)[0]
                            }
                          ]"
                          show-time
                          format="YYYY-MM-DD HH:mm:ss"
                          placeholder="Start"
                          class="mr-2"
                        />
                        <a-date-picker
                          v-decorator="[
                            `discount_periode_variant_end`,
                            {
                              rules: [{
                                required: true,
                                message: 'Discount Periode is Required'
                              }],
                              initialValue: initialValueDiscountVariantDate(rowIndexVariantSku)[1]
                            }
                          ]"
                          show-time
                          format="YYYY-MM-DD HH:mm:ss"
                          placeholder="End"
                        />
                      </a-form-item>
                      <a-form-item class="mt-2">
                        <a-button type="primary" html-type="submit">
                          Submit
                        </a-button>
                      </a-form-item>
                    </a-col>
                  </a-row>
                </a-form>
              </a-modal>
              <a-dropdown v-if="unselectedOptions.length">
                <a-button icon="plus" class="mt-4">Add item</a-button>

                <a-menu slot="overlay">
                  <a-menu-item
                    v-for="(options, index) in unselectedOptions"
                    :key="index" class="add-variant-button__item"
                    @click="addSku(options)"
                  >
                    <span v-for="(option, optIndex) in options" :key="`${index}-${option.value}`">
                      {{ option.value }}
                      <a-icon type="right" v-if="optIndex < options.length - 1" class="mx-2" />
                    </span>
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </div>

            <a-row :gutter="24" type="flex" v-if="!hasVariations">
              <a-col :md="{ span: 8 }">
                <a-form-item
                  label="SKU"
                  class="mb-0"
                >
                  <a-input
                    v-decorator="[
                      'skus.0.sku',
                      {
                        initialValue: skus[0] ? skus[0].sku : null,
                        rules: [{ required: true, message: 'SKU is required' }]
                      }
                    ]"
                  />
                </a-form-item>
              </a-col>
              <a-col :md="{ span: 8 }">
                <a-form-item
                  label="Price"
                  class="mb-0"
                >
                  <a-input
                    :min="0"
                    class="w-100"
                    prefix="Rp"
                    @keypress="e => { if (!/\d/.test(e.key)) return e.preventDefault() }"
                    v-decorator="[
                      'skus.0.price.msrp',
                      {
                        initialValue: skus[0] && skus[0].price.msrp !== 0  ? skus[0].price.msrp : null,
                        rules: [{ required: true, message: 'Price is required' }]
                      }
                    ]"
                  />
                </a-form-item>
              </a-col>
              <a-col :md="{ span: 8 }">
                <a-form-item
                  label="Minimal Qty"
                  class="mb-0"
                >
                  <a-input
                    :min="1"
                    class="w-100"
                    @keypress="e => { if (!/\d/.test(e.key)) return e.preventDefault() }"
                    v-decorator="[
                      'skus.0.minimal_qty',
                      {
                        initialValue: skus[0] ? skus[0].minimal_qty : 1,
                        rules: [{ required: true, message: 'Minimal Qty is required' }]
                      }
                    ]"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row v-if="!hasVariations" class="mt-3">
              <a-col :md="{ span: 12 }">
                <a-form-item label="Is Discounted ?">
                  <a-radio-group
                    v-decorator="[
                      'is_discounted',
                      {
                        initialValue: discountNotVariant ? true : false,
                        rules: [{
                          required: true,
                        }],
                      }
                    ]"
                  >
                    <a-radio :value="true">
                      Yes
                    </a-radio>
                    <a-radio :value="false">
                      No
                    </a-radio>
                  </a-radio-group>
                </a-form-item>
              </a-col>
            </a-row>

            <a-row v-if="form.getFieldValue('is_discounted')" class="mt-3">
              <a-col :md="{ span: 12 }">
                <a-form-item label="Set Discount Type">
                  <a-radio-group v-decorator="[
                    'discount_type',
                    {
                      initialValue: discountNotVariant ? discountNotVariant.discount_type : null,
                      rules: [{
                        required: true
                      }]
                    }]"
                  >
                    <a-radio :value="1">
                      Percentage
                    </a-radio>
                    <a-radio :value="2">
                      Fixed Amount
                    </a-radio>
                  </a-radio-group>
                </a-form-item>
              </a-col>
            </a-row>

            <a-row v-if="form.getFieldValue('is_discounted')">
              <a-col :md="{ span: 8 }">
                <a-form-item
                  label="Discount Value"
                  class="mb-0"
                >
                  <a-input
                    :min="0"
                    class="w-100"
                    v-decorator="[
                      'discount', {
                        initialValue: discountNotVariant ? discountNotVariant.discount_value : null,
                        rules: [{
                          pattern: new RegExp('^[0-9]*$'),
                          message: 'Invalid input',
                          required: true
                        }],
                      }
                    ]"
                    :suffix="form.getFieldValue('discount_type') === 1 ? '%' : null"
                    :prefix="form.getFieldValue('discount_type') === 2 ? 'Rp': null"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row v-if="form.getFieldValue('is_discounted')" class="mt-3">
              <a-col :md="{ span: 8 }">
                <a-form-item
                  label="Discount Periode"
                  class="mb-0"
                >
                  <a-date-picker
                    v-decorator="[
                      `discount_periode_start`,
                      {
                        rules: [{
                          required: true,
                          message: 'Discount Periode is Required'
                        }],
                        initialValue: defaultValueDiscountPeriode() && defaultValueDiscountPeriode()[0]
                      }
                    ]"
                    show-time
                    format="YYYY-MM-DD HH:mm:ss"
                    placeholder="Start"
                    class="mr-2"
                  />
                  <a-date-picker
                    v-decorator="[
                      `discount_periode_end`,
                      {
                        rules: [{
                          required: true,
                          message: 'Discount Periode is Required'
                        }],
                        initialValue: defaultValueDiscountPeriode() && defaultValueDiscountPeriode()[1]
                      }
                    ]"
                    show-time
                    format="YYYY-MM-DD HH:mm:ss"
                    placeholder="End"
                    class="mt-2"
                  />
                  <!-- <a-range-picker
                    v-decorator="[
                      'discount_periode',
                      {
                        rules: [{
                          type: 'array',
                          required: true,
                          message: 'Discount Periode is Required'
                        }],
                        initialValue: defaultValueDiscountPeriode()
                      }
                    ]"
                    dateFormat="DD MM YYYY"
                  /> -->
                </a-form-item>
              </a-col>
            </a-row>

            <div class="mt-5 price-rules" v-if="channelsHasPriceRule.includes(form.getFieldValue('channel'))">
              <h5 class="mb-2">Price rules</h5>

              <div class="price-rules__item" v-for="(sku, skuIndex) in skus" :key="skuIndex">
                <div class="mb-3 price-rules__item__heading">
                  <strong>
                    {{
                      [
                        form.getFieldValue('name'),
                        ...sku.options.map(o => o.value)
                      ].join(' / ')
                    }}
                    - <span class="text-muted">{{ form.getFieldValue(`skus.${skuIndex}.sku`) }}</span>
                  </strong>
                </div>
                <a-row :gutter="24" type="flex" class="px-3" >
                  <a-col v-for="(rule, ruleIndex) in priceLevels" :key="rule.level_id">
                    <a-form-item class="d-none">
                      <a-input
                        type="hidden"
                        v-decorator="[
                          `skus.${skuIndex}.price.level.${ruleIndex}.id`,
                          {
                            initialValue: rule.id,
                            value: rule.id
                          }
                        ]"
                      />
                    </a-form-item>

                    <a-form-item
                      :label="rule.level_name"
                      class="mb-3"
                    >
                      <a-input
                        :min="0"
                        class="text-right"
                        :suffix="form.getFieldValue(`skus.${skuIndex}.price.level.${ruleIndex}.type`) === '1' ? '%' : null"
                        :prefix="form.getFieldValue(`skus.${skuIndex}.price.level.${ruleIndex}.type`) === '2' ? 'Rp' : null"
                        style="width: 250px"
                        @keypress="e => { if (!/\d/.test(e.key)) return e.preventDefault() }"
                        v-decorator="[
                          `skus.${skuIndex}.price.level.${ruleIndex}.value`,
                          {
                            initialValue: getPriceLevelValue(sku, rule).value,
                            rules: [{ required: true, message: 'Price is required' }]
                          }
                        ]"
                      >
                        <a-select
                          slot="addonBefore"
                          v-decorator="[`skus.${skuIndex}.price.level.${ruleIndex}.type`, { initialValue: String(getPriceLevelValue(sku, rule).type) }]"
                          style="width: 125px"
                        >
                          <a-select-option value="1">
                            Percentage
                          </a-select-option>
                          <a-select-option value="2">
                            Absolute
                          </a-select-option>
                        </a-select>
                      </a-input>
                    </a-form-item>
                  </a-col>
                </a-row>
              </div>
            </div>

          </a-card>

          <a-card class="mt-4" id="inventories" v-if="form.getFieldValue('productType') === 'physical'">
            <h5 class="mb-4">Product Stock / Inventories</h5>

            <div
              v-for="warehouse in warehouses"
              :key="warehouse.id"
              class="inventories"
            >
              <table class="ant-table">
                <thead>
                  <tr>
                    <th colspan="3" class="text-uppercase" style="background: #d9e4ff; color: var(--kit-color-primary)">
                      {{ warehouse.name }}

                      <a-popover>
                        <template #content>
                          {{ [
                            warehouse.address,
                            warehouse.subDistrict,
                            warehouse.district,
                            warehouse.city,
                            warehouse.province,
                            warehouse.country
                          ].filter(i => !!i).join(', ') }}
                        </template>
                        <a-icon type="question-circle" theme="filled" class="ml-2"></a-icon>
                      </a-popover>
                    </th>
                  </tr>
                  <tr>
                    <th>Item</th>
                    <th width="50">Pre Order?</th>
                    <th width="100">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(sku, skuIndex) in skus" :key="`${warehouse.id}-${skuIndex}`">
                    <td>
                      <div v-if="!form.getFieldValue('name')" class="text-muted">
                        <i>Fill product name</i>
                      </div>
                      <div>
                        {{
                          [
                            form.getFieldValue('name'),
                            ...sku.options.map(o => o.value)
                          ].join(' / ')
                        }}
                      </div>
                      <div class="text-muted">{{ form.getFieldValue(`skus.${skuIndex}.sku`) }}</div>
                    </td>
                    <td>
                      <a-form-item>
                        <input
                          type="hidden"
                          v-decorator="[
                            `inventories.${warehouse.id}.${skuIndex}.warehouse_id`,
                            {
                              initialValue: warehouse.id
                            }
                          ]"
                        />
                      </a-form-item>
                      <a-form-item>
                        <input
                          type="hidden"
                          v-decorator="[
                            `inventories.${warehouse.id}.${skuIndex}.index`,
                            {
                              initialValue: skuIndex
                            }
                          ]"
                        />
                      </a-form-item>
                      <a-form-item>
                        <a-switch
                          v-decorator="[
                            `inventories.${warehouse.id}.${skuIndex}.preOrder`,
                            {
                              initialValue: product && getInventory(sku, warehouse.id).preorder_enable,
                            }
                          ]"
                          size="small"
                          :checked="form.getFieldValue(`inventories.${warehouse.id}.${skuIndex}.preOrder`)"
                        />
                      </a-form-item>
                      <div class="text-muted small">
                        {{ form.getFieldValue(`inventories.${warehouse.id}.${skuIndex}.preOrder`) ? 'Enabled' : 'Disabled' }}
                      </div>
                    </td>
                    <td class="p-0">
                      <a-form-item>
                        <a-input-number
                          :step="1"
                          :min="0"
                          v-decorator="[
                            `inventories.${warehouse.id}.${skuIndex}.qty`,
                            {
                              /* initialValue: getInventory(sku, warehouse.id).inventory_count || 0, */
                              initialValue: product && getInventory(sku, warehouse.id).inventory_count || 0,
                              rules: [{ required: true, message: 'Field is required' }]
                            }
                          ]"
                        />
                      </a-form-item>
                    </td>
                  </tr>
                </tbody>

              </table>
            </div>
          </a-card>

          <a-card class="mt-4" id="images">
            <h5 class="mb-4">Product Images</h5>

            <h6 class="mb-4 mt-4"> List Image </h6>
            <div class="image-list">
              <div
                v-for="(image, index) in images"
                :key="index"
                class="image-list__wrapper"
              >
                <div class="image-list__item">
                  <div class="image-list__item__img">
                    <img :src="!!image.file ? image.url : `${image.url}?tr=w-150,h-150,cm-pad_resize,bg-FFFFFF`" />

                    <!-- <a-tag color="#4b7cf3" v-if="index === 0">Primary Image</a-tag> -->
                    <a-tag color="#4b7cf3" v-if="index === 0"> Show Image </a-tag>
                    <a-tag color="#4b7cf3" v-if="index === 1"> Thumb Image </a-tag>

                    <div class="image-list__item__img-overlay">
                      <!-- <a-button size="small" v-if="index !== 0" @click="setPrimaryImage(index)">Make primary</a-button> -->
                      <div class="ml-2">
                        <a-button size="small" @click="setShowImg(index)" v-if="index !== 0">Make Show</a-button>
                        <a-button size="small" @click="setThumbImg(index)" v-if="index !== 1" >Make Thumb </a-button>
                      </div>
                      <a-popconfirm
                        title="Are you sure want to remove this image ?"
                        ok-text="Remove"
                        cancel-text="Cancel"
                        @confirm="removeImage(index)"
                        @visibleChange="visible => confirmRemoveImage(index, visible)"
                      >
                        <a-button size="small" icon="delete"></a-button>
                      </a-popconfirm>
                    </div>
                  </div>
                </div>
              </div>

              <div class="image-list__wrapper">
                <div class="image-list__item">
                  <a-upload
                    :show-upload-list="false"
                    name="image"
                    list-type="picture-card"
                    class="image-uploader"
                    :beforeUpload="() => false"
                    @change="addImage"
                    v-decorator="[
                      'image',
                      {
                        rules: [{ required: true }]
                      },
                    ]"
                  >
                    <div>
                      <a-icon type="plus" />
                    </div>
                  </a-upload>
                </div>
              </div>
            </div>
          </a-card>

          <a-card class="mt-4" v-if="form.getFieldValue('productType') === 'physical'" id="shipping">
            <h5 class="mb-4">Shipping Information</h5>

            <a-row type="flex" :gutter="24">
              <a-col :md="{ span: 8 }">
                <a-form-item label="Weight">
                  <a-input-group compact>
                    <a-input-number
                      style="width: 55%"
                      :step="0.1"
                      :min="0.1"
                      v-decorator="[
                        'weight.value',
                        {
                          initialValue: 0,
                          rules: [{ required: true, message: 'Weight is required' }]
                        }
                      ]"
                    />
                    <a-select
                      style="width: 45%"
                      :options="weightType"
                      v-decorator="[
                        'weight.type',
                        {
                          initialValue: 'gram',
                          rules: [{ required: true, message: 'Weight type is required' }]
                        }
                      ]"
                    />
                  </a-input-group>
                </a-form-item>
              </a-col>

              <a-col span="24"></a-col>
              <a-col :md="{ span: 14 }">
                <div class="ant-form-item-label ant-form-item-required">Package weight</div>
                <a-input-group compact>
                  <a-form-item style="width: 20%">
                    <a-input
                      suffix="cm"
                      placeholder="L"
                      :min="0.1"
                      :step="0.1"
                      @keypress="e => { if (!/\d/.test(e.key)) return e.preventDefault() }"
                      v-decorator="[
                        'length',
                        {
                          rules: [{ required: true }]
                        }
                      ]"
                    />
                  </a-form-item>
                  <a-input
                    style="width: 30px"
                    class="px-2 text-center"
                    disabled
                    default-value="X"
                  />
                  <a-form-item style="width: 20%">
                    <a-input
                      suffix="cm"
                      placeholder="W"
                      :min="0.1"
                      :step="0.1"
                      @keypress="e => { if (!/\d/.test(e.key)) return e.preventDefault() }"
                      v-decorator="[
                        'width',
                        {
                          rules: [{ required: true }]
                        }
                      ]"
                    />
                  </a-form-item>
                  <a-input
                    style="width: 30px"
                    class="px-2 text-center"
                    disabled
                    default-value="X"
                  />
                  <a-form-item style="width: 20%">
                    <a-input
                      placeholder="H"
                      suffix="cm"
                      :min="0.1"
                      :step="0.1"
                      @keypress="e => { if (!/\d/.test(e.key)) return e.preventDefault() }"
                      v-decorator="[
                        'height',
                        {
                          rules: [{ required: true }]
                        }
                      ]"
                    />
                  </a-form-item>
                </a-input-group>
              </a-col>
            </a-row>

          </a-card>

          <a-row type="flex" justify="start" class="mt-4 mb-3" v-if="!hideButton">
            <a-button
              type="primary"
              size="large"
              html-type="submit"
            >{{ updateForm ? 'Update Product' : 'Add Product' }}</a-button>
          </a-row>
        </a-col>

        <a-col :xs="{ span: 0 }" :lg="{ span: 6 }">
          <a-anchor :offset-top="80" :target-offset="100">
            <a-anchor-link href="#info" title="Product Information" />
            <a-anchor-link href="#description" title="Product Description" />
            <a-anchor-link href="#variant" title="Variant" />
            <a-anchor-link href="#management" title="Product Management" v-if="!hasVariations" />
            <a-anchor-link href="#images" title="Images" />
            <a-anchor-link href="#shipping" title="Shipping Information" v-if="form.getFieldValue('productType') === 'physical'" />
          </a-anchor>
        </a-col>
      </a-row>
    </a-form>
  </a-spin>
</template>

<script>
/* eslint-disable */

import { mapGetters, mapState } from 'vuex'
import startsWith from 'lodash/startsWith'
import flatten from 'lodash/flatten'
import TextEditor from '@/components/Input/TextEditor'
import { getImagePreview } from '@/services/image/utils'
import { getCategoryTreeById } from '@/store/catalogue/getters'
import moment from 'moment'

const generateOptionId = (opts = []) => opts.join('-').toLowerCase()
const extractOptionId = id => id.split('-').map(i => Number(i))
const positionOptionId = id => extractOptionId(id).reduce((acc, current) => acc * current, 1)
const getOptionId = (opts = [], variantOptions = []) => generateOptionId(
  (opts || []).reduce((result, opt, index) => {
    const variant = variantOptions.find(i => i.index === index)

    if (opt && variant) {
      // const valueIndex = variant.values.indexOf(opt)
      // console.log({ v: variant.values, opts, opt })

      result.push(String(opt).toLowerCase())
    }
    return result
  }, []),
)
const getVariantOptionByCatalogOption = (opts = []) => (opts || []).reduce((acc, item, index) => {
  if (item) {
    const variant = VARIANT_OPTIONS.find(variant => variant.index === index)
    variant.values = [item]

    acc.push(variant)
  }

  return acc
}, [])

const getVariantOptionsByCatalogs = (catalog = []) => {
  // const options = []
  return catalog.reduce((result, item, index) => {
    if (index === 0) {
      result.push(...getVariantOptionByCatalogOption(item.option))
    } else {
      (item.option || []).forEach((opt, index) => {
        if (opt) {
          const variant = result.find(variant => variant.index === index)
          if (variant && !variant.values.includes(opt)) {
            variant.values.push(opt)
          }
        }
      })
    }

    return result
  }, [])
}

const VARIANT_OPTIONS = [
  {
    label: 'Color',
    index: 0,
    id: 'color',
    placeholder: 'Enter color variant option, eg. Black',
    values: [],
  },
  {
    label: 'Size',
    index: 1,
    id: 'size',
    placeholder: 'Enter size variant option. eg. XL',
    values: [],
  },
  {
    label: 'Flavour',
    index: 2,
    id: 'Flavour',
    placeholder: 'Enter flavour variant option. eg. Strawberry',
    values: [],
  },
]

export default {
  name: 'ProductForm',
  components: {
    TextEditor,
  },
  props: {
    updateForm: {
      type: Boolean,
      default: false,
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default() {
        return null
      },
    },
    discount: {
      type: Object,
      default() {
        return null
      },
    },
  },
  data() {
    return {
      productTypes: [
        {
          value: 'physical',
          title: 'Physical Product',
        },
        {
          value: 'digital',
          title: 'Digital Product',
        },
      ],
      weightType: [
        {
          value: 'gram',
          title: 'gram',
        },
        // {
        //   value: 'kg',
        //   title: 'kg',
        // },
      ],
      selectedOptions: [],
      skus: [],
      images: [],
      showImage: [],
      thumbImage: [],
      indexShowImg: null,
      indexThumbImg: null,
      id: null,
      saving: false,
      form: this.$form.createForm(this),
      catalog_list: [],
      isDiscount: false,
      modalDiscount: false,
      rowIndexVariantSku: 0,
      formDiscountVarian: this.$form.createForm(this),
      discountVariants: [],
      discountNotVariant: null,
      skusEditDelete: null,
      initialValueDiscountVariant: null,
      isHasDicount: false,
    }
  },
  mounted() {
    this.fillInitialFormValue()

    this.$store.dispatch('catalogue/getCategories')
    this.$store.dispatch('channel/getChannels')
    this.$store.dispatch('warehouse/get')
    this.$store.dispatch('catalogue/getVendorAttributes')
  },
  computed: {
    ...mapGetters('catalogue', {
      categories: 'categoryTreeOptions',
      categoryList: 'categories',
    }),
    ...mapGetters('channel', {
      channels: 'options',
      channelsHasPriceRule: 'hasLevelPrice',
    }),
    ...mapState('channel', {
      priceLevels: 'priceLevels',
    }),
    ...mapGetters('warehouse', {
      warehouses: 'list',
    }),
    ...mapState('catalogue', {
      productAttributes: state => state.productAttributes,
      vendorProductAttributes: state => state.vendorProductAttributes,
    }),
    productAttributesUsed () {
      return this.productAttributes.length && this.id ? this.productAttributes : this.vendorProductAttributes
    },
    layout() {
      return {
        span: this.hideAnchor ? 24 : 18,
      }
    },
    hasVariations() {
      return !!this.selectedOptions.length
    },
    availableVariantOptions() {
      return VARIANT_OPTIONS.filter((variant) => !this.selectedOptionIndex.includes(variant.index))
    },
    selectedOptionIndex() {
      return this.selectedOptions.map(item => item.index)
    },
    rowOptionHeight() {
      const lengths = this.selectedOptions.map(item => item.values.length)

      return lengths.map((_, index) => lengths.slice(index + 1).reduce((accumulator, current) => accumulator * current, 1))
    },
    optionTree() {
      return this.getOptionTree()
    },
    unselectedOptions() {
      const indexes = this.skus.map(item => item.optionId)
      const options = this.getSkuListFromTree(this.optionTree)
        .filter(item => !indexes.includes(item.optionId))
        .map(item => item.options)
      //   .map((item, index, items) => item.map((i, j) => items[j][index]))

      // const getChildren = (parent = 0) => {
      //   const children = getChildren(parent + 1)

      //   return options[parent].map(item => {

      //   })
      // }

      // // // eslint-disable-next-line no-unused-vars
      // const result = getValueByIndex(0)

      return options
    },
    skusThatHasPriceRules() {
      return this.skus.filter(({ price }) => price.level && price.level.length)
    },
  },
  watch: {
    product: {
      deep: true,
      handler: function(val) {
        this.fillInitialFormValue()
      },
    },
    discount: {
      deep: true,
      handler: function(val) {
        this.fillInitialFormValue()
      },
    },
    categoryList: function() {
      this.setDefaultCategory()
    },
    optionTree: {
      deep: true,
      immediate: true,
      handler: function(val) {
        const skuOption = {
          id: null,
          sku: '',
          name: '',
          price: {
            msrp: 0,
            level: [],
          },
          options: [],
          optionId: '',
        }

        this.$nextTick(() => {
          if (val.length) {
            this.skus = this.getSkuListFromTree(val).map(({ optionId, options, ...item }) => {
              const sku = this.skus.find(sku => sku.optionId === optionId)

              return {
                ...skuOption,
                ...item,
                ...sku,
                optionId,
                options,
              }
            })
          } else {
            this.skus = [
              {
                ...skuOption,
                ...this.skus[0],
              },
            ]
          }
        })
      },
    },
    channels: {
      immediate: true,
      handler: function (val) {
        if (this.form && val && val.length && !this.form.getFieldValue('channel')) this.form.setFieldsValue({ channel: val[0].value })
      },
    },
  },
  methods: {
    fillInitialFormValue() {
      if (this.product || this.discount) {
        const { is_physical, all_images, id, title, description, catalog_list, price_list: priceList } = this.product
        const defaultDescription = description && description.length ? description[0] : {}
        const catalog = catalog_list && catalog_list.length ? catalog_list[0] : {}
        // eslint-disable-next-line no-unused-vars
        const variantOptions = getVariantOptionsByCatalogs(catalog_list || [])

        this.id = id

        const channel = priceList && priceList.length ? priceList[0].channel_id : null

        const skus = catalog_list.map(({ sku, catalog_id, uuid, option }) => {
          // get price from priceList base on channel
          const price = priceList.find(({ channel_id, catalog_id: priceCatalogId }) => channel_id === channel && priceCatalogId === catalog_id)

          return {
            sku,
            id: uuid,
            uuid,
            optionId: getOptionId(option, variantOptions),
            options: [],
            price: {
              msrp: price ? price.price_msrp : 0,
              level: price ? price.levels : [],
            },
            minimal_qty: price.minimal_qty,
          }
        }).sort((a, b) => positionOptionId(a.optionId) - positionOptionId(b.optionId))

        this.skus = skus
        this.selectedOptions = variantOptions
        this.catalog_list = catalog_list

        if (this.discount) {
          if (this.discount.discount_list.length === 1 && !this.hasVariations) {
            this.discountNotVariant = {
              discount_value: this.discount.discount_list[0].discount_value,
              valid_from: this.discount.discount_list[0].valid_from,
              valid_until: this.discount.discount_list[0].valid_until,
              discount_type: this.discount.discount_list[0].discount_type,
            }
          } else if (this.hasVariations) {
            this.discountVariants = this.discount.discount_list.map(item => {
              const setDiscVariant = {
                discount_variant: item.discount_value,
                catalog_sku: item.catalog_sku,
                discount_periode_variant: [item.valid_from, item.valid_until],
                discount_variant_type: Number(item.discount_type),
              }
              return setDiscVariant
            })
          }
        }

        if (this.id) {
          this.$store.dispatch('catalogue/getProductAttributes', {
            uuid: this.id,
          })
            .then(() => {
              this.$nextTick()
            })
        }

        this.$nextTick(() => {
          this.form.setFieldsValue({
            name: title,
            description: defaultDescription.longDesc,
            shortDescription: defaultDescription.shortDesc,
            'weight.value': catalog.product_weight,
            height: catalog.product_height,
            width: catalog.product_width,
            length: catalog.product_length,
            productType: is_physical ? 'physical' : 'digital',
            // ...this.skus.reduce((acc, item, index) => {
            //   acc[`skus.${index}.sku`] = item.sku
            //   acc[`skus.${index}.price`] = item.price

            //   return acc
            // }, {}),
          })
          this.setDefaultCategory()
        })

        this.$nextTick(() => {
          this.form.setFieldsValue({
            channel,
          })
        })

        this.images = all_images
        const showImage = all_images.filter((img, key) => img.is_show === 1)
        const thumbImage = all_images.filter((img, key) => img.is_thumb === 1)
        const images = this.images.filter((img, key) => img.is_show === 0 && img.is_thumb === 0)

        this.images = [...showImage, ...thumbImage, ...images]
      }
    },
    setDefaultCategory() {
      if (!this.product || !this.product.category_id) return

      const val = getCategoryTreeById(this.product.category_id, this.categoryList)
      this.form.setFieldsValue({
        category: val,
      })
    },
    addVariant({ key }) {
      const variant = VARIANT_OPTIONS[key]
      variant.values = ['']
      this.selectedOptions.push(variant)
    },
    addVariantOption(variant) {
      variant.values.push('')
    },
    removeVariantOption(variant, key) {
      if (variant.values.length === 1) {
        this.selectedOptions = this.selectedOptions.filter(item => item.index !== variant.index)
      } else {
        variant.values = variant.values.filter((_, index) => index !== key)
      }
    },
    canRemoveVariantOption(variant, key) {
      return true
    },
    getOptionTree(parent = [], index = 0) {
      const tree = []
      const nextIndex = index + 1

      if (!this.selectedOptions[index]) return tree

      const { values, ...option } = this.selectedOptions[index] || {}

      values.forEach((optionValue, optionValueIndex) => {
        const opts = [...parent, {
          index: optionValueIndex,
          value: optionValue,
        }]

        const optionTreeItem = {
          ...option,
          value: optionValue,
          id: generateOptionId(opts.map(item => item.value ? String(item.value).toLowerCase() : null)),
          children: this.getOptionTree(opts, nextIndex),
        }

        tree.push(optionTreeItem)
      })

      return tree
    },
    getSkuListFromTree(tree = [], parentOptions = []) {
      const list = []

      tree.forEach(({ children, ...option }) => {
        const opts = [...parentOptions, option]
        const childrenList = this.getSkuListFromTree(children, opts)

        if (childrenList && childrenList.length) {
          list.push(...childrenList)
        } else {
          list.push({
            optionId: option.id,
            options: opts,
          })
        }
      })

      return list
    },
    deleteSku(index) {
      this.skus = this.skus.filter((_, key) => key !== index)
    },
    addSku(options) {
      const option = options[options.length - 1]
      const rowIndex = option.id.split('-').reduce((result, val) => result + Number(val), 0)

      const sku = {
        options,
        optionId: option.id,
        sku: '',
        price: {
          msrp: 0,
          level: [],
        },
      }

      this.skus.splice(rowIndex, 0, sku)
      this.$nextTick(() => {
        document.getElementById(`skus[${rowIndex}][sku]`).focus()
      })
    },
    inUnselectedOptions(option) {
      return this.unselectedOptions.filter(item => startsWith(item[item.length - 1].id, option.id)).length
    },
    getInventory(sku, warehouse_id, event) {
      // const sortingWarehouse = this.product.inventories.filter(item => item.warehouse_id === warehouse_id)
      if (!this.product || !this.product.inventories.length || !this.catalog_list?.length) {
        return {}
      }

      const getProductBasedSku = this.catalog_list.find(item => item.sku === sku.sku)
      if (!getProductBasedSku) {
        return {}
      }

      const inventory = getProductBasedSku.inventories.find(item => item.warehouse_id === warehouse_id)
      if (!inventory) {
        return {}
      }

      return {
        preorder_enable: !!inventory.preorder_enable,
        inventory_count: inventory.inventory_count,
      }
    },
    addImage({ file }) {
      getImagePreview(file)
        .then(url => {
          this.images.push({
            file,
            url,
          })
        })
    },
    addShowImage({ file }) {
      getImagePreview(file)
        .then(url => {
          this.showImage.push({
            file,
            url,
          })
        })
    },
    addThumbImage({ file }) {
      getImagePreview(file)
        .then(url => {
          this.thumbImage.push({
            file,
            url,
          })
        })
    },
    removeImage(index) {
      this.images = this.images.filter((_, key) => key !== index)
    },
    removeShowImage(index, image) {
      this.showImage = this.showImage.filter((_, key) => key !== index)
    },
    removeThumbImage(index, image) {
      this.thumbImage = this.thumbImage.filter((_, key) => key !== index)
    },
    confirmRemoveImage(index, visible) {
      const image = this.images[index]
      if (visible && !image.id) {
        this.removeImage(index)
      }
    },
    confirmRemoveShowImage (index, visible) {
      const image = this.showImage[index]

      if (visible && !image.id) {
        this.removeShowImage(index)
      }
    },
    confirmRemoveThumbImage (index, visible) {
      const image = this.thumbImage[index]

      if (visible && !image.id) {
        this.removeThumbImage(index)
      }
    },
    setShowImg (index) {
      if (index === 1) {
        const showImage = this.images[index]
        const thumbImage = this.images[0]
        const images = this.images.filter((img, key) => {
          if (key !== index && key !== 0 && key !== 1) {
            return img
          }
        })
        this.images = this.images.length === 2 ? [showImage, thumbImage] : [showImage, thumbImage, ...images]
      } else {
        const showImage = this.images[index]
        const thumbImage = this.images[1]
        const images = this.images.filter((img, key) => {
          if (key !== index && key !== 1) {
            return img
          }
        })
        this.images = [showImage, thumbImage, ...images]
      }
    },
    setThumbImg (index) {
      if (index === 0) {
        const showImage = this.images[1]
        const thumbImage = this.images[index]
        const images = this.images.filter((img, key) => {
          if (key !== index && key !== 0 && key !== 1) {
            return img
          }
        })
        this.images = this.images.length === 2 ? [showImage, thumbImage] : [showImage, thumbImage, ...images]
      } else {
        const showImage = this.images[0]
        const thumbImage = this.images[index]
        const images = this.images.filter((img, key) => {
          if (key !== index && key !== 0) {
            return img
          }
        })
        this.images = [showImage, thumbImage, ...images]
      }
    },
    setPrimaryImage(index) {
      const image = this.images[index]
      const images = this.images.filter((_, key) => key !== index)

      this.images = [image, ...images]
    },
    getPriceLevelValue({ price }, rule) {
      return price.level.find(level => level.id === rule.id) || {
        id: rule.id,
        value: rule.default_discount,
        type: '1',
      }
    },
    submitDiscountVariant () {
      this.formDiscountVarian.validateFieldsAndScroll((err, values) => {
        if (err) return undefined
        this.modalDiscount = false
        this.initialValueDiscountVariant = null
        const discountVariant = { ...values }
        discountVariant.discount_periode_variant = [values.discount_periode_variant_start, values.discount_periode_variant_end]
        discountVariant.rowIndex = this.rowIndexVariantSku
        if (this.hasDiscountVariant(this.rowIndexVariantSku)) {
          const editDiscount = this.discountVariants.map(item => item.rowIndex === this.rowIndexVariantSku || item.catalog_sku === this.skusEditDelete ? discountVariant : item)
          this.discountVariants = editDiscount
        } else {
          this.discountVariants.push(discountVariant)
        }
        this.formDiscountVarian.resetFields()
      })
    },
    hasDiscountVariant (rowIndex) {
      const findDiscount = this.discountVariants.find(item => item.rowIndex === rowIndex || this.form.getFieldValue(`skus.${rowIndex}.sku`) === item.catalog_sku)
      if (!findDiscount) return false
      return true
    },
    setInitialValueDiscountVariant (rowIndex) {
      if (this.discountVariants.length > 0 && this.hasDiscountVariant(rowIndex)) {
        const findDiscount = this.discountVariants.find(item => item.rowIndex === rowIndex || this.form.getFieldValue(`skus.${rowIndex}.sku`) === item.catalog_sku)
        this.initialValueDiscountVariant = {
          value: findDiscount.discount_variant,
          type: findDiscount.discount_variant_type,
        }
      } else if (this.discountVariants.length > 0 && !this.hasDiscountVariant(this.rowIndexVariantSku)) {
        return ''
      } else {
        return ''
      }
    },
    valueDiscountVariant (rowIndex) {
      const findDiscount = this.discountVariants.find(item => item.rowIndex === rowIndex || this.form.getFieldValue(`skus.${rowIndex}.sku`) === item.catalog_sku)
      if (findDiscount) {
        return {
          value: findDiscount.discount_variant,
          type: findDiscount.discount_variant_type,
        }
      }
      return {}
    },
    initialValueDiscountVariantDate (rowIndex) {
      const findDiscount = this.discountVariants.find(item => item.rowIndex === rowIndex || this.form.getFieldValue(`skus.${rowIndex}.sku`) === item.catalog_sku)
      // discount_periode_variant
      const date = []
      if (findDiscount) {
        const fromDate = findDiscount.rowIndex === 0 || findDiscount.rowIndex ? moment(findDiscount.discount_periode_variant[0]).format('YYYY-MM-DD HH:mm:ss') : moment(findDiscount.discount_periode_variant[0] * 1000).format('YYYY-MM-DD HH:mm:ss')
        const untilDate = findDiscount.rowIndex === 0 || findDiscount.rowIndex ? moment(findDiscount.discount_periode_variant[1]).format('YYYY-MM-DD HH:mm:ss') : moment(findDiscount.discount_periode_variant[1] * 1000).format('YYYY-MM-DD HH:mm:ss')
        date.push(moment(fromDate, 'YYYY-MM-DD HH:mm:ss'))
        date.push(moment(untilDate, 'YYYY-MM-DD HH:mm:ss'))
      }
      return date
    },
    changeDateFormatDiscountVariant (rowIndex) {
      const findDiscount = this.discountVariants.find(item => item.rowIndex === rowIndex || this.form.getFieldValue(`skus.${rowIndex}.sku`) === item.catalog_sku)
      const valid_from = findDiscount.rowIndex === 0 || findDiscount.rowIndex ? moment(findDiscount.discount_periode_variant[0]).format('YYYY-MM-DD HH:mm:ss') : moment(findDiscount.discount_periode_variant[0] * 1000).format('YYYY-MM-DD HH:mm:ss')
      const valid_to = findDiscount.rowIndex === 0 || findDiscount.rowIndex ? moment(findDiscount.discount_periode_variant[1]).format('YYYY-MM-DD HH:mm:ss') : moment(findDiscount.discount_periode_variant[1] * 1000).format('YYYY-MM-DD HH:mm:ss')
      return `${valid_from} - ${valid_to}`
    },
    deleteDiscountVariant () {
      const deleteDiscount = this.discountVariants.filter((item) => item.rowIndex || item.rowIndex === 0 ? item.rowIndex !== this.rowIndexVariantSku : item.catalog_sku !== this.skusEditDelete)
      this.discountVariants = deleteDiscount
    },
    defaultValueDiscountPeriode () {
      if (!this.discountNotVariant) {
        return null
      }
      const date = []
      const fromDate = moment(this.discountNotVariant.valid_from * 1000).format('YYYY-MM-DD HH:mm:ss')
      const untilDate = moment(this.discountNotVariant.valid_until * 1000).format('YYYY-MM-DD HH:mm:ss')
      date.push(moment(fromDate, 'YYYY-MM-DD HH:mm:ss'))
      date.push(moment(untilDate, 'YYYY-MM-DD HH:mm:ss'))
      return date
    },
    valueTableDiscountVariant (rowIndex) {
      if (this.valueDiscountVariant(rowIndex).type === 1) {
        const value = this.form.getFieldValue(`skus.${rowIndex}.price.msrp`) - ((this.valueDiscountVariant(rowIndex).value / 100) * this.form.getFieldValue(`skus.${rowIndex}.price.msrp`))
        return `${this.valueDiscountVariant(rowIndex).value} % (Rp. ${value})`
      } else if (this.valueDiscountVariant(rowIndex).type === 2) {
        const value = this.form.getFieldValue(`skus.${rowIndex}.price.msrp`) - this.valueDiscountVariant(rowIndex).value
        return `Rp. ${this.valueDiscountVariant(rowIndex).value} (Rp. ${value})`
      }
    },
    submit() {
      this.form.validateFieldsAndScroll((errors, { skus, inventories, ...values }) => {
        if (errors) return undefined

        this.saving = true

        const formatedSkus = this.skus.map((sku, index) => ({
          ...sku,
          ...skus[index],
        }))

        const discount_list = []
        if (this.discountVariants.length > 0 || this.form.getFieldValue('discount')) {
          if (!this.hasVariations) {
            const disc = {
              catalog_sku: skus[0].sku,
              discount_value: Number(values.discount),
              discount_type: this.form.getFieldValue('discount_type'),
              valid_from: moment(values.discount_periode_start).unix(),
              valid_until: moment(values.discount_periode_end).unix(),
            }
            discount_list.push(disc)
          } else {
            for (let i = 0; i < formatedSkus.length; i++) {
              const findDiscount = this.discountVariants.find(item => item.rowIndex === i || item.catalog_sku === formatedSkus[i].sku)
              if (findDiscount) {
                const disc = {
                  catalog_sku: formatedSkus[i].sku,
                  discount_value: Number(findDiscount.discount_variant),
                  discount_type: Number(findDiscount.discount_variant_type),
                  valid_from: moment(findDiscount.discount_periode_variant[0]).unix(),
                  valid_until: moment(findDiscount.discount_periode_variant[1]).unix(),
                }
                discount_list.push(disc)
              }
            }
          }
        }

        const parsingDateAttributes = (param) => {
          return this.$moment(param).format('X')
        }

        const attributes = this.productAttributesUsed.length ? this.productAttributesUsed.map((item, index) => {
          console.log(Object.keys(values), "<<<<< object keys values")
          const test = Object.keys(values).find(element => Number(element) === item.attribute_id)
          console.log(test, 'testnya')
          if (Object.keys(values).find(element => Number(element) === item.attribute_id)) {
            const param = {
              attribute_id: item.attribute_id,
              productattribute_value: item.attribute_type === 'date' ? parsingDateAttributes(values[item.attribute_id]) : item.attribute_type === 'rangedate' ? [parsingDateAttributes(values[item.attribute_id][0]), parsingDateAttributes(values[item.attribute_id][1])] : values[item.attribute_id],
              attribute_name: item.attribute_name,
              attribute_slug: item.attribute_slug,
              attribute_type: item.attribute_type,
              productattribute_id: item.productattribute_id ? item.productattribute_id : null,
            }

            return param
          }
        }) : []

        this.$store.dispatch('catalogue/save', {
          ...values,
          id: this.id,
          skus: formatedSkus,
          images: [...this.images],
          // images: [...this.showImage, ...this.thumbImage, ...this.images],
          inventories: inventories ? flatten(Object.values(inventories)).map(({ index, ...item }) => ({
            ...item,
            sku: formatedSkus[index].sku,
          })) : [],
          discount_list,
          attributes,
        }).then(
          (data) => {
            this.saving = false
            this.$emit('saved', data)
          },
          (err) => {
            this.saving = false
            this.$notification.error({
              message: err.response?.data?.message || err.message,
            })
          },
        )
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.product {
  &-name {
    input {
      border-color: #c5d0e6;
      height: 45px;
      font-size: 1.2rem;
    }
  }
}

.no-border {
  border: 0 !important
}

.variant-items {
  &__wrapper {
    display: flex;
    margin: 0 -16px;
  }

  &__item {
    border: 1px solid #e4e9f0;
    border-radius: 2px;
    padding: 1.5rem 1rem 1rem 1rem;
    margin-top: 1.5rem;
    position: relative;

    legend {
      position: absolute;
      top: 0;
      margin-bottom: 0;
      border: 0;
      background: #fff;
      transform: translateY(-50%);
      display: inline-block;
      width: auto;
      text-transform: uppercase;
      padding: .5rem .5rem;
      font-size: 14px;
      color: #595c97;
    }

    &__footer {
      > .remove {
        color: #f15c65;
        border-color: #fcbfc2;

        &:hover {
          border-color: #f15c65;
        }
      }
    }

    &__option {
      margin-bottom: 1rem;

      button {
        width: 25px;
        height: 25px;
        font-size: 1rem;

        &:disabled {
          cursor: not-allowed;
        }
      }
    }
  }
}

.variant-table {
  margin-top: 2rem;

  table {
    width: 100%;

    tr > th,
    tr > td {
      border: 1px solid #e9e9f3;
      padding: .5rem 1rem;
      text-align: center;
    }

    tr > td.input-cell {
      padding: 0;
    }

    tr > th.price {
      width: 125px
    }
    tr > th.sku {
      width: 150px
    }
  }

  thead {
    tr > th {
      background: #e9e9f3;
      padding: .5rem 1rem;
    }
  }
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  margin: -8px;

  &__wrapper {
    padding: 8px;
  }

  &__item {

    &__img {
      width: 140px;
      height: 140px;
      position: relative;
      display: flex;
      align-items: center;
      border: 1px solid #e9e9f3;

      .ant-tag {
        position: absolute;
        top: .5rem;
        left: -.5rem;
        transition: all .1s ease-in;
      }

      &-overlay {
        width: 100%;
        height: 100%;
        position: absolute;
        opacity: 0;
        transition: all .1s ease-in-out;
        background: rgba(0, 0, 0, .5);
        top: 0;
        left: 0;
        z-index: 10;
        padding: .5rem;
        display: flex;
        justify-content: flex-end;
        align-items: flex-end;
      }

      &:hover {
        .ant-tag {
          left: .5rem;
          z-index: 11;
        }
      }

      &:hover & {
        &-overlay {
          opacity: 1;
        }
      }
    }
  }

  button {
    font-size: 85% !important;
    margin-left: .25rem;
    margin-right: .25rem;
    height: 20px !important;

    &.ant-btn-icon-only {
      width: 20px !important;
    }
  }

  img {
    max-width: 100%;
    width: 100%;
    max-height: 100%;
  }
}

.price-rules {

  &__item {
    margin-bottom: 2rem;
    border: 1px solid #e3ecff;

    &__heading {
      background: #c1d4ff;
      padding: .5rem 1rem;
    }
  }
}

// .add-variant-button__item {
//   span:not(:last-child) {
//     &::after {
//       content: '/'
//     }
//   }
// }
.inventories {
  table {
    width: 100%;
    margin-bottom: 2rem;

    tr {
      th,
      td {
        padding: .5rem 1rem;
        border: 1px solid #e9e9f3;
        vertical-align: middle;
      }

      th {
        background: #f5f5fa;
        text-align: center;
      }
    }

    .ant-form-item {
      padding: 0;
      margin-bottom: 0;

    }

    .ant-input,
    .ant-input-number {
      border: 0;
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
.variant-items__item__option {
  .ant-input-group-addon {
    background-color: transparent;
  }
}

.variant-table {
  .ant-form-item {
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
  }

  .ant-input {
    border: 0;
    text-align: center;

    &:focus {
      text-align: left;
    }

    &-number {
      width: 100%;
      border: 0;

      &-input {
        border: 0;
        text-align: center;

        &:focus {
          text-align: left;
        }
      }

      &-handler-wrap {
        display: none;
      }
    }
  }

  .ant-form-explain {
    position: absolute;
    background: #f15c65;
    color: #fff !important;
    padding: .15rem .75rem;
    font-size: 80%;
    height: auto;
    z-index: 1;
    border-radius: 2px;
  }
}

.image-uploader {
  .ant-upload-select-picture-card {
    margin: 0 !important;
    width: 140px !important;
    height: 140px !important;

    img {
      max-width: 100%
    }

    i {
      font-size: 32px;
      color: #999;
    }

    .ant-upload-text {
      margin-top: 8px;
      color: #666;
    }
  }
}
</style>
